import React from "react";
//#region redux
import { useDispatch } from "react-redux";
import { setListNavigation } from "store/actions/listActions";
//#endregion
//#region types
import {
  ColumnVisibility,
  ListAPIResponse,
  TableAsyncProps,
  TableHandles,
  TableRowCommonKeys,
  TableRowProperties,
} from "models/propTypes/simpleTableProps";
import { WintUtilsWebApi } from "wint-api-client/dist/types";
import { CommonConstants, IconDefinitionConstants } from "utils/constants";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TableSortLabel, Grid } from "@mui/material";
import useTableContext from "./context/useTableContext";
import useAPIRequestKeyRef from "../useAPIRequestKeyRef";
import useMountedState from "../useMountedState";
import useValueFormatter from "./useValueFormatter";
import useTableEntityNavigation from "./useTableEntityNavigation";
import LoadingWithLogo from "../loading/loadingWithLogo";
import CheckBox from "../forms/baseFields/checkBox";
import Icon from "../icon";
import InfoTooltip from "../tooltips/infoTooltip";
import TableContentRow from "./tableContentRow";
import TableFooter from "./tableFooter";
import * as Skeletons from "../skeletons";
import { pascalize } from "humps";
import { useOrderByDescState, useOrderByState, usePageState, useRowsPerPageState } from "./useTableStates";
import useAsyncTableExport from "./useAsyncTableExport";
import useTableHelper from "./useTableHelper";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { LocalizationHelperInstance, TableHelper } from "utils/helpers";

function TableAsyncInner<RowType extends Record<string, any>>(
  props: React.PropsWithChildren<TableAsyncProps<RowType>>
) {
  const tableContext = useTableContext();
  const {
    pagination = true,
    properties,
    filter,
    hidePaginationIfSinglePage,
    disableSort,
    reloadKey: reload,
    thenByProperty,
    includeSummary,
    fetchSummaryWithData,
    customSummaryInfo,
    customSummaryInfoForExport,
    allowCheckBoxSelection,
    onRowSelectionsChange,
    hiddenColumns,
    tableId,
    itemUrl,
    disableQueryPagination,
    stayOnCurrentPage,
    pageResetKey,
    initialPage,
    onCurrentPageChange,
    exportFileName,
    exportFileDescription,
    pdfHeaderTextSmall,
  } = props;

  const { apiRequestKeyRef, updateKey } = useAPIRequestKeyRef();
  const { apiRequestKeyRef: apiSummaryRequestKeyRef, updateKey: updateSummaryKey } = useAPIRequestKeyRef();
  const { getMounted } = useMountedState();
  const { getTableColumnTitle } = useTableHelper();

  const dispatch = useDispatch();

  const [loading, setLoading] = React.useState(false);
  const [total, setTotal] = React.useState(0);
  const [totalWithoutFilter, setTotalWithoutFilter] = React.useState(0);
  const [results, setResults] = React.useState<(RowType & TableRowCommonKeys)[]>([]);
  const [summaryRow, setSummaryRow] = React.useState<RowType>();

  const [page, setPage] = usePageState(tableId, disableQueryPagination, initialPage);
  const [rowsPerPage, setRowsPerPage] = useRowsPerPageState(
    props.maxRowCount ?? CommonConstants.DefaultTableRowCount,
    tableId,
    disableQueryPagination
  );
  const [orderByProperty, setOrderByProperty] = useOrderByState(
    (props.thenByProperty ?? props.orderByProperty ?? Object.keys(properties)[0]) as string,
    tableId,
    disableQueryPagination
  );
  const [orderByDescending, setOrderByDescending] = useOrderByDescState(
    props.thenByProperty ? !!props.thenByDescending : !!props.orderByDescending,
    tableId,
    disableQueryPagination
  );

  const [showHelpText, setShowHelpText] = React.useState<boolean>(false);
  const [selectedAll, setSelectedAll] = React.useState<boolean>(false);

  const { getSumamryFormattedValue } = useValueFormatter();

  const prevFilterRef = React.useRef(filter);
  React.useEffect(() => {
    prevFilterRef.current = filter;
  });
  const prevFilter = prevFilterRef.current;

  const prevPageResetKeyRef = React.useRef(pageResetKey);
  React.useEffect(() => {
    prevPageResetKeyRef.current = pageResetKey;
  });
  const prevPageResetKey = prevPageResetKeyRef.current;

  React.useEffect(() => {
    const filterChanged = prevFilter !== filter;
    if (page !== 0 && ((!stayOnCurrentPage && filterChanged) || prevPageResetKey !== pageResetKey)) {
      setPage(0);
      return;
    }
    // A removed row, or a remembered page from a longer list, can point past the end, so fall back to the last page.
    if (stayOnCurrentPage && Array.isArray(filter)) {
      const lastPage = Math.max(0, Math.ceil(filter.length / rowsPerPage) - 1);
      if (page > lastPage) {
        setPage(lastPage);
        return;
      }
    }
    loadResults();
  }, [page, rowsPerPage, orderByProperty, orderByDescending, filter, reload, pageResetKey]);

  React.useEffect(() => {
    onCurrentPageChange?.(page);
  }, [page]);

  const getApiFilter = (page: number, overrideRowsPerPage?: number) => {
    const allowThenBy =
      thenByProperty &&
      orderByProperty !== props.orderByProperty &&
      (!props.disableGroupWhenOrderBy?.length || props.disableGroupWhenOrderBy?.every((d) => d !== orderByProperty));

    // Check if we should apply group-based sorting
    const shouldUseGroupSorting =
      props.groupByProperty &&
      properties.group &&
      (!props.disableGroupWhenOrderBy?.length || props.disableGroupWhenOrderBy?.every((d) => d !== orderByProperty));

    const apiFilter: WintUtilsWebApi.Filter = {
      page: page,
      numPerPage: overrideRowsPerPage ?? rowsPerPage,
      orderByProperty: pascalize(
        shouldUseGroupSorting
          ? (props.groupByProperty as string)
          : allowThenBy
            ? (props.orderByProperty as string)
            : (orderByProperty as string)
      ),
      orderByDescending: shouldUseGroupSorting
        ? !!props.groupByDescending
        : allowThenBy
          ? !!props.orderByDescending
          : orderByDescending,
      thenByProperty: shouldUseGroupSorting
        ? pascalize(orderByProperty as string)
        : allowThenBy
          ? pascalize(orderByProperty as string)
          : undefined,
      thenByDescending: shouldUseGroupSorting ? orderByDescending : allowThenBy ? orderByDescending : false,
      isAutoPaginating: false,
      includeSummary: fetchSummaryWithData && includeSummary,
    };

    return apiFilter;
  };

  const getColumnHeader = (value?: TableRowProperties) => {
    const title = value?.description && getTableColumnTitle(value.description);
    if (!value?.helpTooltip) return title;
    return (
      <span className={cssExports.tableHeaderTitleWithTooltip}>
        {title}
        <span className={cssExports.tooltipIconAlign} onClick={(e) => e.stopPropagation()}>
          <InfoTooltip text={LocalizationHelperInstance.getFormattedMessage(value.helpTooltip)} />
        </span>
      </span>
    );
  };

  const loadResults = () => {
    const apiFilter: WintUtilsWebApi.Filter = getApiFilter(page);

    if (typeof filter === "function") {
      const filterHandler = filter as <T extends WintUtilsWebApi.Filter>(
        filter: T,
        key: number
      ) => ListAPIResponse<any>;

      setLoading(true);
      const currentKey = updateKey();
      filterHandler(apiFilter, currentKey)
        .then((res) => ({ data: res.data, key: res.key }))
        .then((results) => {
          if (getMounted() && (results.key === undefined || apiRequestKeyRef.current === results.key)) {
            setResults(results?.data.items ?? []);
            setTotal(results?.data.totalItems ?? 0);
            updateCheckBoxSelection(results.data.items);

            // If fetching summary with data in a single request, extract summary info here
            if (fetchSummaryWithData && includeSummary) {
              setSummaryRow(results.data.summaryItem);
              setTotalWithoutFilter(results.data.totalItemsWithOutFilter);
            }
          }
        })
        .catch((error) => {
          if (getMounted()) {
            console.error("Error during filter table values: ", error);
            setTotal(0);
            setResults([]);
            updateCheckBoxSelection([]);
          }
        })
        .finally(() => setLoading(false));
    } else {
      const currentPage = page + 1;
      const items = (
        pagination ? filter.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage) : filter
      ) as RowType[];
      setResults(
        disableSort
          ? items
          : TableHelper.tableSortAsync(
              items,
              TableHelper.getComparator(orderByDescending ? "desc" : "asc", orderByProperty as string)
            )
      );
      setTotal(filter.length);
      updateCheckBoxSelection(items);
    }

    if (
      tableContext &&
      tableContext.tableKey === tableId &&
      JSON.stringify(tableContext.filter) !== JSON.stringify(apiFilter)
    ) {
      tableContext.setFilter(apiFilter);
    }
  };

  React.useEffect(() => {
    // Only run separate summary request if fetchSummaryWithData is false/undefined
    if (includeSummary && !fetchSummaryWithData) {
      const summaryFilter: WintUtilsWebApi.Filter = {
        page: page,
        numPerPage: rowsPerPage,
        orderByDescending: orderByDescending,
        isAutoPaginating: false,
        includeSummary: includeSummary,
        thenByDescending: false,
      };

      if (typeof props.filter === "function") {
        const filterHandler = props.filter as <T extends WintUtilsWebApi.Filter>(
          filter: T,
          key: number
        ) => ListAPIResponse<any>;

        const currentKey = updateSummaryKey();
        filterHandler(summaryFilter, currentKey)
          .then((res) => ({ data: res.data, key: res.key }))
          .then((results) => {
            if (getMounted() && (results.key === undefined || apiSummaryRequestKeyRef.current === results.key)) {
              setSummaryRow(results.data.summaryItem);
              setTotalWithoutFilter(results.data.totalItemsWithOutFilter);
            }
          });
      }
    }
  }, [reload, filter]);

  const onPageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const onRowsPerPageChange = (rowsPerPage: number) => {
    setPage(0);
    setRowsPerPage(rowsPerPage);
  };

  const onSortLabelClick = (key: string) => {
    if (orderByProperty === key) setOrderByDescending(!orderByDescending);
    else {
      setOrderByProperty(key);

      const columnIsNumber = [
        "date",
        "month",
        "year",
        "time",
        "value",
        "amount",
        "number",
        "count",
        "percentage",
        "cost",
        "price",
        "quantity",
      ].some((c) => key.toLowerCase().endsWith(c));
      setOrderByDescending(columnIsNumber);
    }
  };

  const onToggleHelpTextClick = () => {
    setShowHelpText(!showHelpText);
  };

  const updateCheckBoxSelection = (items?: any[]) => {
    if (props.allowCheckBoxSelection) {
      const allSelected = items ? items?.every((i) => i.select) : false;
      setSelectedAll(allSelected);
    }
  };

  const toggleSelection = (rowIndex: number) => {
    if (props.disableCheckBoxSelectionForRows) return;

    const updatedRows = [...results];
    const updatedToggleValue = !updatedRows[rowIndex].select;
    updatedRows[rowIndex].select = updatedToggleValue;
    setResults(updatedRows);
    if (selectedAll) {
      !updatedToggleValue && setSelectedAll(false);
    } else {
      updatedRows.every((r) => r.select) && setSelectedAll(true);
    }
    onRowSelectionsChange &&
      onRowSelectionsChange(
        typeof filter === "function" ? updatedRows.filter((r) => r.select) : filter.filter((r) => r.select)
      );
  };

  const toggleSelectAll = () => {
    const toggleValue = !selectedAll;
    const updatedRows = [...results];
    updatedRows.forEach((r) => (r.select = toggleValue));
    setSelectedAll(toggleValue);
    setResults(updatedRows);

    onRowSelectionsChange &&
      onRowSelectionsChange(toggleValue ? (typeof filter === "function" ? updatedRows : filter) : []);
  };

  const hiddenColumnsSet = new Set(hiddenColumns);
  const propertiesList = Object.entries(properties);
  const displayProperties = propertiesList.filter(([key, val]) =>
    val?.visibility !== undefined ? val.visibility === ColumnVisibility.Default : !hiddenColumnsSet.has(key)
  );
  const columnCount = properties.group ? displayProperties.length - 1 : displayProperties.length;
  const columns = displayProperties.filter(([key, value]) => key !== "group");
  const exportColumns = propertiesList.filter(([key, val]) => val?.visibility !== ColumnVisibility.Hidden);
  const summaryIndex = displayProperties.findIndex(([, value]) => value?.summaryValueFormatter);
  const summaryColumns = displayProperties.slice(summaryIndex, columnCount);

  const includeSummaryForExport = summaryIndex !== -1 && summaryRow !== undefined;

  const rowHoverActions = (row: any, rowIndex: number) => {
    return props.rowHoverActionsHandler ? props.rowHoverActionsHandler(row, rowIndex) : [];
  };

  const { goToItem } = useTableEntityNavigation();

  const onRowClick = (row: any, rowIndex: number) => {
    if (props.rowClickHandler) {
      props.rowClickHandler(row, rowIndex, page, rowsPerPage, getApiFilter);
    }

    if (itemUrl && row.id && row.id !== CommonConstants.EmptyGuid) {
      goToItem(row.id, itemUrl, false);

      dispatch(
        setListNavigation({
          navigationIds: results.map((i) => i.id),
          indexToOpen: rowIndex,
          pageToOpen: page,
          total: total,
          numPerPage: rowsPerPage,
          loadNext: (page: number) => {
            if (typeof filter === "function") {
              const filterHandler = filter as <T extends WintUtilsWebApi.Filter>(
                filter: T,
                key: number
              ) => ListAPIResponse<any>;
              return filterHandler(getApiFilter(page), 0).then((res) => ({
                items: res?.data.items?.map((i) => i.id) ?? [],
                total: res?.data.totalItems ?? 0,
              }));
            } else {
              return new Promise<{ items: (number | string)[]; total: number }>((resolve) =>
                resolve({ items: [], total: 0 })
              );
            }
          },
          itemUrl: itemUrl,
        })
      );
    } else {
      dispatch(setListNavigation({}));
    }
  };

  //#region export
  const { exportReport, exportRawData } = useAsyncTableExport(
    exportColumns,
    filter,
    getApiFilter,
    orderByDescending,
    orderByProperty,
    disableSort,
    exportFileName,
    exportFileDescription,
    pdfHeaderTextSmall,
    includeSummaryForExport,
    includeSummaryForExport ? summaryRow : undefined,
    includeSummaryForExport ? customSummaryInfoForExport : undefined,
    includeSummaryForExport ? summaryColumns : undefined,
    includeSummaryForExport ? total : undefined,
    includeSummaryForExport ? totalWithoutFilter : undefined
  );

  React.useImperativeHandle(
    props.tableRef,
    (): TableHandles => ({
      exportToExcel: exportReport.bind(null, "xlsx"),
      exportToCSV: exportReport.bind(null, "csv"),
      exportToPDF: exportReport.bind(null, "pdf"),
      exportRawDataToExcel: exportRawData.bind(null, "xlsx"),
      exportRawDataToCSV: exportRawData.bind(null, "csv"),
      importCSVToArticle: function (): String {
        throw new Error("Function not implemented.");
      },
      getApiFilter: getApiFilter,
    })
  );
  //#endregion

  return (
    <Grid
      item
      container
      className={clsx(
        cssExports.tableContainerOuter,
        cssExports.simpleTable,
        props.alternateColors && cssExports.alternatingColoredTable,
        props.className
      )}
      data-testid={`${tableId}TableContainer`}
    >
      {loading && results.length ? <LoadingWithLogo className={cssExports.loadingList} /> : <></>}
      {loading && !results.length ? (
        <Skeletons.TableSkeleton />
      ) : (
        <TableContainer classes={{ root: cssExports.tableContainerRoot }}>
          <Table aria-label="simple table">
            <TableHead className={clsx(cssExports.tableHead, props.hideHeader && cssExports.hidden)}>
              <TableRow>
                {allowCheckBoxSelection && (
                  <TableCell className={clsx(cssExports.tableCell, cssExports.emptyCell, cssExports.select)}>
                    <CheckBox checked={selectedAll} onChange={toggleSelectAll} />
                  </TableCell>
                )}
                {columns.map(([headerKey, value], index) => (
                  <TableCell
                    key={`tab-header-${index}`}
                    className={cssExports.tableCell}
                    align={value?.alignment}
                    sortDirection={
                      orderByProperty === headerKey && !disableSort && !value?.disableSort
                        ? orderByDescending
                          ? "desc"
                          : "asc"
                        : false
                    }
                    style={{
                      width: value?.width,
                      maxWidth: value?.maxWidth,
                    }}
                    onClick={disableSort || !!value?.disableSort ? undefined : onSortLabelClick.bind(null, headerKey)}
                  >
                    {disableSort || !!value?.disableSort ? (
                      getColumnHeader(value)
                    ) : (
                      <TableSortLabel
                        active={orderByProperty === headerKey}
                        direction={
                          orderByProperty === headerKey
                            ? orderByDescending
                              ? "desc"
                              : "asc"
                            : headerKey.toLowerCase().endsWith("date")
                              ? "desc"
                              : "asc"
                        }
                        classes={{
                          icon: cssExports.tableSortLabelIcon,
                          active: cssExports.tableSortLabelActive,
                        }}
                      >
                        {getColumnHeader(value)}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
              {showHelpText && (
                <TableRow className={cssExports.tableHeaderHelpText}>
                  {columns.map(([, value], index) => {
                    return (
                      <TableCell
                        key={`tab-helpText-${index}`}
                        className={cssExports.tableCell}
                        align={value?.alignment}
                      >
                        {value?.helpText && LocalizationHelperInstance.getFormattedMessage(value.helpText)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              )}

              {columns.some(([, value]) => !!value?.helpText) && (
                <TableRow>
                  <TableCell colSpan={columnCount} className={cssExports.helpTextIconCell}>
                    <span onClick={onToggleHelpTextClick}>
                      <Icon
                        className={cssExports.tableHelpTextIcon}
                        icon={
                          showHelpText ? IconDefinitionConstants.WInfoExpanded : IconDefinitionConstants.WInfoCircleFill
                        }
                      />
                    </span>
                  </TableCell>
                </TableRow>
              )}
            </TableHead>
            <TableBody className={cssExports.tableBody}>
              {results.length > 0 &&
                results.map((row, index, array) => {
                  return (
                    <React.Fragment key={`tab-row-${index}`}>
                      {properties.group &&
                        (!props.disableGroupWhenOrderBy ||
                          props.disableGroupWhenOrderBy.every((key) => key !== orderByProperty)) &&
                        (index === 0 ||
                          (properties.group.formatter
                            ? properties.group.formatter(row, index) !==
                              properties.group.formatter(array[index - 1], index - 1)
                            : row["group"] !== array[index - 1]["group"])) && (
                          <TableRow className={cssExports.tableSubHead}>
                            <TableCell
                              colSpan={columnCount + (allowCheckBoxSelection ? 1 : 0)}
                              className={cssExports.tableCell}
                            >
                              {properties.group.formatter ? properties.group.formatter(row, index) : row["group"]}
                            </TableCell>
                          </TableRow>
                        )}
                      <TableContentRow
                        row={row}
                        index={index}
                        columns={columns}
                        rowClickHandler={onRowClick}
                        rowHoverActions={rowHoverActions(row, index)}
                        toggleSelectionHandler={toggleSelection}
                        allowCheckBoxSelection={allowCheckBoxSelection}
                        disableCheckBoxSelection={props.disableCheckBoxSelectionForRows}
                        loading={row.loading}
                        rowClasses={props.rowClasses}
                        data-testid={`${tableId}TableRow-${index}`}
                      />
                    </React.Fragment>
                  );
                })}
              {pagination && (
                <TableRow data-testid={`tableFooterRow`}>
                  <TableCell
                    className={cssExports.tableFooterCell}
                    colSpan={columnCount + (allowCheckBoxSelection ? 1 : 0)}
                    data-testid={`tableFooterCell`}
                  >
                    <TableFooter
                      total={total}
                      rowsPerPage={rowsPerPage}
                      currentPage={page}
                      advancedPagination={pagination === "advanced"}
                      changePageHandler={onPageChange}
                      changeRowsPerPageHandler={onRowsPerPageChange}
                      hideIfSinglePage={hidePaginationIfSinglePage}
                    />
                  </TableCell>
                </TableRow>
              )}
              {summaryIndex !== -1 && summaryRow && (
                <TableRow className={cssExports.tableSummaryRow} data-testid={`tableSummaryRow`}>
                  <TableCell colSpan={summaryIndex}>
                    <Grid container>
                      {customSummaryInfo ? (
                        typeof customSummaryInfo === "function" ? (
                          customSummaryInfo(summaryRow)
                        ) : (
                          customSummaryInfo
                        )
                      ) : (
                        <>
                          <Grid item>
                            <Icon icon={IconDefinitionConstants.WInfoCircleFill} className={cssExports.summaryIcon} />
                          </Grid>
                          <Grid item xs className={cssExports.summaryText}>
                            {LocalizationHelperInstance.getFormattedMessage(
                              TranslationKeys.Common.SummaryShowsFilteredData,
                              undefined,
                              {
                                currentTotal: `${total}`,
                                totalWithoutFilter: `${totalWithoutFilter}`,
                              }
                            )}
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </TableCell>
                  {summaryColumns.map(([key, cellValue], cellIndex) => (
                    <TableCell
                      key={`tab-summaryText-${cellIndex}`}
                      align={cellValue?.alignment}
                      data-testid={`tableSummaryTextCell-${key}`}
                    >
                      {getSumamryFormattedValue(
                        cellValue?.summaryValueFormatter ? cellValue.summaryValueFormatter(summaryRow) : ""
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Grid>
  );
}

export default TableAsyncInner;
